import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getMenuProps } from 'components/ContextMenu';
import { DataTags } from 'components/DataTags';
import LoadingWrapper from 'components/LoadingWrapper';
import { useOrganizationFilterBinding } from 'components/ObjectTable/useOrganizationOptions';
import dayjs from 'dayjs';
import { useDefaultTableProps } from 'hooks/useDefaultTableProps';
import { useDeleteConfirmation } from 'hooks/useDeleteConfirmation';
import { useTableDimensions } from 'hooks/useDimensions';
import { useTableSelection } from 'hooks/useTableSelection';
import { useTableTitle } from 'hooks/useTableTitle';
import { getText } from 'i18n/intl';
import {
  capitalize,
  chain,
  intersection,
  isArray,
  isEqual,
  isUndefined,
  omitBy,
  startCase,
} from 'lodash';
import { userSelector } from 'slices/auth.slice';
import { authConfigsMapSelector } from 'slices/authConfig.slice';
import { organizationSelector } from 'slices/organization.slice';
import {
  organizationNodesSelector,
  organizationTreeSelector,
} from 'slices/organizationHierarchy.slice';
import {
  appPermissionsSelector,
  scopesSelector,
} from 'slices/permission.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import {
  MAX_RESULTS,
  activateUser,
  activateUsersLoadingSelector,
  checkFirstUserExists,
  createUnsavedUser,
  deactivateUser,
  deactivateUsersLoadingSelector,
  deleteUser,
  duplicateUser,
  fetchMoreUsers,
  fetchTotalUsers,
  fetchUsersSuccess,
  firstSelectedUserSelector,
  setFindUserPayload,
  setFirstSelectedUser,
  setSecondSelectedUser,
  setUserActionFilter,
  shouldReloadSelector,
  totalUsersSelector,
  usersMapSelector,
  usersSelector,
} from 'slices/user.slice';
import type {
  TFindManyTEUsersBody,
  TOrganizationTreeNode,
  TTEUser,
} from '@timeedit/types';
import {
  type Column,
  type ColumnConfig,
  ConfirmationPopup,
  EditColumnsDialog,
  EditableTable,
  Filter,
  type GetMore,
  Icon,
  type OnEditColumns,
  type TEIFilter,
  Tag,
  useInfiniteScroll,
} from '@timeedit/ui-components';
import {
  Button,
  Dropdown,
  Flex,
  type MenuProps,
  type TableProps,
  Tooltip,
  Typography,
} from '@timeedit/ui-components/antd';
import type { SorterResult } from '@timeedit/ui-components/antd/table/interface';
import { useSavedTableFilters } from '../../../hooks/useSavedTableFilters';
import { getLocalDisplayFormatFromTimestamp } from '../../../utils/timeConverter';
import {
  filterFields,
  getVisibilityOptions,
  lockedOptions,
  optionalFilterFields,
} from './userDataConfig';
import './UserList.scss';

const ORG_FILTER_FIELDS = ['standardOrganization'] as const;

type UserListProps = {
  confirmDiscardUserChanges: (
    isFirstDrawer: boolean,
    actionFn: () => void
  ) => void;
  onRowSelect: (users: TTEUser[]) => void;
};

const UserTable = ({
  confirmDiscardUserChanges,
  onRowSelect,
}: UserListProps) => {
  const dispatch = useAppDispatch();

  const authConfigsMap = useAppSelector(authConfigsMapSelector);
  const scopes = useAppSelector(scopesSelector);
  const appPermissions = useAppSelector(appPermissionsSelector);
  const users = useAppSelector(usersSelector);
  const usersMap = useAppSelector(usersMapSelector);
  const totalUsers = useAppSelector(totalUsersSelector);
  const shouldReload = useAppSelector(shouldReloadSelector);
  const firstUser = useAppSelector(firstSelectedUserSelector);
  const authUser = useAppSelector(userSelector);
  const allOrganizationNodes = useAppSelector(organizationNodesSelector);
  const organization = useAppSelector(organizationSelector);
  const deactivateUsersLoading = useAppSelector(deactivateUsersLoadingSelector);
  const activateUsersLoading = useAppSelector(activateUsersLoadingSelector);

  const shouldSeeHiddenUsers = useMemo(
    () =>
      intersection(authUser?.scopes, ['TE_ADMIN::super', 'TE_ROOT_USER'])
        .length > 0,
    [authUser]
  );

  const [filterSettings, setFilterSettings] = useState<TEIFilter>(() => ({
    filters: { active: true },
    sorts: {},
    freeTextSearch: '',
  }));

  const { savedFilters, onSaveFilter, onDeleteFilter } =
    useSavedTableFilters('userTable');

  const { options: organizationOptions, filterProps: orgFilterProps } =
    useOrganizationFilterBinding({
      isParentDisabled: true,
      fields: ORG_FILTER_FIELDS,
      settings: filterSettings,
      onChange: setFilterSettings,
      savedFilters,
      onSaveFilter,
    });

  const [sortBy, setSortBy] = useState<string>('');

  const authConfigOptions = Object.entries(authConfigsMap).map(
    ([key, value]: [any, any]) => {
      return {
        value: key,
        label: value.name,
      };
    }
  );

  const scopeOptions = scopes.map((scopeData: any) => {
    return {
      value: scopeData.value,
      label: scopeData.name,
    };
  });

  const appPermissionsOptions = appPermissions.map((appPermission: any) => {
    return {
      value: appPermission.value,
      label: appPermission.name,
    };
  });

  const organizationNodeOptions = useMemo(
    () =>
      chain(allOrganizationNodes)
        .filter((node) => node.id != null)
        .map((node) => ({
          value: node.id!,
          label: node.name,
        }))
        .value(),
    [allOrganizationNodes]
  );

  const onCreate = useCallback(
    () =>
      confirmDiscardUserChanges(true, () => {
        dispatch(createUnsavedUser());
      }),
    [confirmDiscardUserChanges, dispatch]
  );

  const onDeactivate = useCallback(
    () => dispatch(deactivateUser()),
    [dispatch]
  );

  const onActivate = useCallback(() => dispatch(activateUser()), [dispatch]);

  const onRow = useCallback<Exclude<TableProps<TTEUser>['onRow'], undefined>>(
    (user: TTEUser) => {
      return {
        onClick: () => {
          confirmDiscardUserChanges(true, () => {
            dispatch(setFirstSelectedUser(user));
          });
        },
      };
    },
    [confirmDiscardUserChanges, dispatch]
  );

  const handleTableChange = useCallback<
    Exclude<TableProps<TTEUser>['onChange'], undefined>
  >((_, __, sorter) => {
    setSortBy(combineSorters(Array.isArray(sorter) ? sorter : [sorter]));
  }, []);

  const columns = useMemo(() => getColumns(authConfigsMap), [authConfigsMap]);

  const [columnConfigs, setColumnConfigs] = useState<ColumnConfig[]>(() =>
    columns
      .map((column) => {
        const key = column.key as string;
        return {
          hidden: column.hidden ?? false,
          key,
          label:
            typeof column.title === 'string' ? column.title : startCase(key),
        };
      })
      .filter((c) => Boolean(c.label))
  );

  const updatedColumns = useMemo(() => {
    return columns.map((column) => {
      if (!column.key) {
        return column;
      }

      const config = columnConfigs.find((c) => c.key === column.key);
      return {
        ...column,
        resizable: true,
        hidden: config?.hidden,
      };
    });
  }, [columnConfigs, columns]);

  const [contentRef, defaultTableProps] = useDefaultTableProps<TTEUser>(60);

  const { height: tableHeight } = useTableDimensions(contentRef);

  const tableRow = document.querySelector('.ant-table-row');
  const itemHeight = tableRow?.clientHeight ?? 50;

  // Remove undefined filter values added when a filter is added or removed to keep from refetching unnecessarily
  const [compactFilters, setCompactFilters] = useState<TEIFilter['filters']>({
    active: true,
  });
  useEffect(() => {
    setCompactFilters((previous) => {
      const newFilters = omitBy(filterSettings.filters, isUndefined);
      if (isEqual(previous, newFilters)) return previous;
      return newFilters;
    });
  }, [filterSettings.filters]);

  // TODO: This logic should likely be removed when updating Filter component
  const updatedFilters = useMemo(() => {
    const {
      appPermissions,
      lockedForExternalAuthentication,
      scopes,
      lastLoginAt,
      ...newFilters
    } = compactFilters;

    // Currently a string in the filter, but the BE expects an array
    if (appPermissions && typeof appPermissions === 'string') {
      newFilters.appPermissions = appPermissions.split(',');
    }

    // The BE expects "locked", but the property is named "lockedForExternalAuthentication"
    if (lockedForExternalAuthentication !== undefined) {
      newFilters.locked = lockedForExternalAuthentication;
    }

    // Currently a string in the filter, but the BE expects an array
    if (scopes && typeof scopes === 'string') {
      newFilters.scopes = scopes.split(',');
    }

    if (lastLoginAt && isArray(lastLoginAt)) {
      const [from, to] = lastLoginAt;
      newFilters.lastLoginAt = {
        from: dayjs(from).startOf('day').unix(),
        to: dayjs(to).endOf('day').unix(),
      };
    }

    return newFilters;
  }, [compactFilters]);

  const findManyFilterBody = useMemo<TFindManyTEUsersBody>(() => {
    const findManyFilterBody = {
      filter: updatedFilters,
      limit: MAX_RESULTS,
      page: 1,
      searchString: filterSettings.freeTextSearch,
      sortBy,
      locale: sortBy ? organization?.language || 'en' : undefined,
    };
    dispatch(setFindUserPayload(findManyFilterBody));
    return findManyFilterBody;
  }, [
    dispatch,
    filterSettings.freeTextSearch,
    sortBy,
    updatedFilters,
    organization,
  ]);

  const {
    idSelectionMode,
    includedIds: includedUserIds,
    excludedIds: excludedUserIds,
    allSelected,
    rowSelection,
  } = useTableSelection(users, onRowSelect);

  useEffect(() => {
    const payload: TFindManyTEUsersBody = {
      filter: {
        ...findManyFilterBody.filter,
      },
    };

    if (findManyFilterBody.searchString) {
      payload.searchString = findManyFilterBody.searchString;
    }

    if (findManyFilterBody.sortBy) {
      payload.sortBy = findManyFilterBody.sortBy;
      payload.locale = organization?.language || 'en';
    }

    if (idSelectionMode === 'select' && includedUserIds.length) {
      payload.filter.ids = includedUserIds;
    } else if (idSelectionMode === 'deselect' && excludedUserIds.length) {
      payload.filter.excludedIds = excludedUserIds;
    }

    dispatch(setUserActionFilter(payload));
  }, [
    dispatch,
    excludedUserIds,
    findManyFilterBody,
    idSelectionMode,
    includedUserIds,
    organization,
  ]);

  const getMore = useCallback<GetMore<TTEUser>>(
    async (options) => {
      const { results, page, totalPages, totalResults } = await dispatch(
        fetchMoreUsers({
          ...findManyFilterBody,
          ...options,
        })
      ).unwrap();

      return {
        data: results,
        page,
        count: results.length,
        total: totalResults,
        hasMore: results.length === MAX_RESULTS && page < totalPages,
      };
    },
    [dispatch, findManyFilterBody]
  );

  const { loading, onScroll, reset, total } = useInfiniteScroll<TTEUser>({
    itemHeight,
    containerHeight: tableHeight,
    getMore,
    limit: MAX_RESULTS,
    virtual: true,
  });

  const selectedUsersCount = useMemo(() => {
    if (idSelectionMode === 'select') {
      return includedUserIds.length;
    }

    return total - excludedUserIds.length;
  }, [excludedUserIds, idSelectionMode, includedUserIds, total]);

  const shouldDisableBulkAction = useCallback(
    (targetInactive: boolean) => {
      if (selectedUsersCount === 0) return true;

      // Disable if the filter already excludes the target users
      if (findManyFilterBody.filter.active === targetInactive) return true;

      if (idSelectionMode !== 'select') return false;

      return !includedUserIds.some((userId) => {
        const user = usersMap?.[userId];
        return targetInactive
          ? user?.inactiveSince != null
          : user?.inactiveSince == null;
      });
    },
    [
      idSelectionMode,
      includedUserIds,
      selectedUsersCount,
      usersMap,
      findManyFilterBody,
    ]
  );

  const shouldDisableDeactivateUsers = useMemo(
    () => shouldDisableBulkAction(false),
    [shouldDisableBulkAction]
  );
  const shouldDisableActivateUsers = useMemo(
    () => shouldDisableBulkAction(true),
    [shouldDisableBulkAction]
  );

  const FooterDisplayCount = useCallback(() => {
    const selectedCount = allSelected ? total : selectedUsersCount;

    if (selectedCount === 0) {
      return null;
    }

    return <Typography.Text>{`${selectedCount} selected`}</Typography.Text>;
  }, [allSelected, selectedUsersCount, total]);

  const [isEditColumnsDialogOpen, setIsEditColumnsDialogOpen] = useState(false);

  const handleClickEditColumns = useCallback(() => {
    setIsEditColumnsDialogOpen(true);
  }, []);

  const closeEditColumnsDialog = useCallback(() => {
    setIsEditColumnsDialogOpen(false);
  }, []);

  const handleEditColumns = useCallback<OnEditColumns>((configs) => {
    setIsEditColumnsDialogOpen(false);
    setColumnConfigs(configs);
  }, []);

  /** Used to bind sub-menu confirm to the outer button */
  const dropdownConfirmButton = useRef<HTMLButtonElement | HTMLAnchorElement>(
    null
  );

  const editUserMenu: MenuProps = useMemo(
    () => ({
      items: [
        createBulkActionMenuItem({
          key: 'activateUser',
          label: 'Activate users',
          message:
            'Are you sure you want to activate the selected users? This action will also activate the user profiles associated with these users.',
          disabled: shouldDisableActivateUsers || !!activateUsersLoading,
          onConfirm: onActivate,
          getPopupContainer: () => dropdownConfirmButton.current,
        }),
        createBulkActionMenuItem({
          key: 'deactivateUser',
          label: 'Deactivate users',
          message:
            'Are you sure you want to deactivate the selected users? This action will also deactivate the user profiles associated with these users.',
          disabled: shouldDisableDeactivateUsers || !!deactivateUsersLoading,
          onConfirm: onDeactivate,
          getPopupContainer: () => dropdownConfirmButton.current,
        }),
      ],
    }),
    [
      shouldDisableDeactivateUsers,
      deactivateUsersLoading,
      onDeactivate,
      shouldDisableActivateUsers,
      activateUsersLoading,
      onActivate,
      dropdownConfirmButton,
    ]
  );

  const footer = useCallback(
    () => (
      <Flex style={{ width: '100%' }} justify="space-between">
        <Flex gap="small" justify="flex-start">
          <Button size="small" type="primary" onClick={onCreate}>
            Create new user
          </Button>
          <Dropdown menu={editUserMenu} trigger={['click']}>
            <Button size="small" ref={dropdownConfirmButton}>
              Edit
            </Button>
          </Dropdown>
          <FooterDisplayCount />
        </Flex>
      </Flex>
    ),
    [onCreate, FooterDisplayCount, editUserMenu]
  );

  const titleActions = useMemo(
    () => ({
      right: (
        <Button size="small" onClick={handleClickEditColumns}>
          {getText('ui.table.edit_columns')}
        </Button>
      ),
    }),
    [handleClickEditColumns]
  );

  const title = useTableTitle<TTEUser>({
    total: firstUser?.id === 'new' ? totalUsers + 1 : totalUsers,
    filteredAmount: firstUser?.id === 'new' ? total + 1 : total,
    actions: titleActions,
  });

  useEffect(() => {
    dispatch(fetchTotalUsers());
  }, [dispatch]);

  // Trigger a reset when getMore changes (e.g. when filters change)
  useEffect(() => {
    dispatch(fetchUsersSuccess([]));
    reset();
  }, [dispatch, getMore, reset, shouldReload]);

  const onCompare = useCallback(
    (user: TTEUser) => {
      if (!dispatch(checkFirstUserExists())) {
        return dispatch(setFirstSelectedUser(user));
      }
      confirmDiscardUserChanges(false, () => {
        dispatch(setSecondSelectedUser(user));
      });
    },
    [confirmDiscardUserChanges, dispatch]
  );

  const onDuplicate = useCallback(
    (user: TTEUser) => {
      confirmDiscardUserChanges(true, () => {
        dispatch(duplicateUser(user));
      });
    },
    [confirmDiscardUserChanges, dispatch]
  );

  const [confirmDelete, contextHolder] = useDeleteConfirmation();

  const onDelete = useCallback(
    (user: TTEUser) => {
      confirmDelete(user.email, () => {
        dispatch(deleteUser(user.id));
      });
    },
    [confirmDelete, dispatch]
  );

  const checkDrawerOpen = useCallback(
    () => dispatch(checkFirstUserExists()),
    [dispatch]
  );

  const menuProps = useCallback(
    (user: TTEUser) =>
      getMenuProps({
        record: user,
        onCompare,
        onDuplicate,
        onDelete,
        checkDrawerOpen,
        title: user.username,
      }),
    [checkDrawerOpen, onCompare, onDelete, onDuplicate]
  );

  return (
    <div className="user-list" ref={contentRef}>
      <Filter
        {...orgFilterProps}
        options={{
          visibility: getVisibilityOptions(shouldSeeHiddenUsers),
          authConfigId: authConfigOptions,
          scopes: scopeOptions,
          appPermissions: appPermissionsOptions,
          locked: lockedOptions,
          standardOrganization: organizationOptions,
          organizationNode: organizationNodeOptions,
        }}
        defaultFields={filterFields}
        optionalFields={optionalFilterFields}
        searchPlacement="beforeFilters"
        onDeleteFilter={onDeleteFilter}
      />
      <LoadingWrapper isLoading={loading}>
        <EditableTable
          dataSource={users}
          columns={updatedColumns}
          getMenuProps={menuProps}
          footer={footer}
          onChange={handleTableChange}
          onRow={onRow}
          onScroll={onScroll}
          rowSelection={rowSelection}
          title={title}
          {...defaultTableProps}
        />
      </LoadingWrapper>
      <EditColumnsDialog
        columnConfigs={columnConfigs}
        onCancel={closeEditColumnsDialog}
        onConfirm={handleEditColumns}
        open={isEditColumnsDialogOpen}
        styles={{
          body: {
            padding: '20px 24px',
          },
        }}
      />
      {contextHolder}
    </div>
  );
};

export default UserTable;

const getColumns = (authConfigsMap: Record<string, any>): Column<TTEUser>[] => {
  return [
    {
      title: 'First name',
      dataIndex: 'firstName',
      key: 'firstName',
      ellipsis: true,
      sorter: { multiple: 1 },
      width: 100,
      fixed: 'left',
    },
    {
      title: 'Last name',
      dataIndex: 'lastName',
      key: 'lastName',
      ellipsis: true,
      sorter: { multiple: 1 },
      width: 100,
      fixed: 'left',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      ellipsis: true,
      sorter: { multiple: 1 },
      width: 160,
    },
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
      ellipsis: true,
      sorter: { multiple: 1 },
      width: 160,
    },
    {
      title: 'Auth config',
      dataIndex: 'authConfigId',
      key: 'authConfigId',
      render: (authConfigId: any) => {
        const authConfig = authConfigsMap[authConfigId];
        return authConfig ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
            }}
          >
            <Typography.Text ellipsis>{authConfig.name}</Typography.Text>
          </div>
        ) : (
          '-'
        );
      },
      sorter: { multiple: 1 },
      width: 100,
    },
    {
      title: 'Locked',
      dataIndex: 'lockedForExternalAuthentication',
      width: 80,
      key: 'lockedForExternalAuthentication',
      render: (lockedForExternalAuthentication: any) => {
        return lockedForExternalAuthentication ? 'Yes' : 'Not locked';
      },
      sorter: { multiple: 1 },
    },
    {
      title: 'Active',
      width: 80,
      dataIndex: 'inactiveSince',
      key: 'inactiveSince',
      ellipsis: true,
      sorter: { multiple: 1 },
      render: (inactiveSince: any) => {
        return inactiveSince ? (
          <Tooltip
            title={`Inactive since ${dayjs(inactiveSince * 1000).format(
              'YYYY-MM-DD'
            )}`}
          >
            <Tag style={{ height: 21 }} color="red">
              Inactive
            </Tag>
          </Tooltip>
        ) : (
          <Tag style={{ height: 21 }} color="green">
            Active
          </Tag>
        );
      },
    },
    {
      title: 'Visibility',
      dataIndex: 'visibility',
      key: 'visibility',
      width: 70,
      render: (visibility: any) => {
        return (
          <Typography.Text>
            {capitalize(visibility.replace('_', ' '))}
          </Typography.Text>
        );
      },
      sorter: { multiple: 1 },
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 100,
      ellipsis: true,
      sorter: { multiple: 1 },
      render: (createdAt: number) =>
        getLocalDisplayFormatFromTimestamp(createdAt, 'sec'),
    },
    {
      title: 'Last modified',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 100,
      ellipsis: true,
      sorter: { multiple: 1 },
      render: (updatedAt: number) =>
        getLocalDisplayFormatFromTimestamp(updatedAt, 'sec'),
    },
    {
      title: 'Last Login',
      dataIndex: 'lastLoginAt',
      key: 'lastLoginAt',
      width: 100,
      ellipsis: true,
      sorter: { multiple: 1 },
      render: (lastLoginAt: number) =>
        getLocalDisplayFormatFromTimestamp(lastLoginAt, 'sec'),
    },
    {
      title: 'Scopes',
      dataIndex: 'scopes',
      key: 'scopes',
      render: (scopes: string[]) => <DataTags data={scopes} />,
      sorter: false,
      width: 190,
    },
    {
      title: 'App Permissions',
      dataIndex: 'appPermissions',
      key: 'appPermissions',
      render: (appPermissions: string[]) => <DataTags data={appPermissions} />,
      sorter: false,
      width: 205,
      fixed: 'right',
    },
  ];
};

const createBulkActionMenuItem = ({
  key,
  label,
  message: confirmMessage,
  disabled,
  onConfirm,
  getPopupContainer,
}: {
  key: string;
  label: string;
  message: string;
  disabled: boolean;
  onConfirm: () => void;
  getPopupContainer: (triggerNode: HTMLElement) => HTMLElement;
}) => ({
  key,
  disabled,
  label: disabled ? (
    <span>{label}</span>
  ) : (
    <ConfirmationPopup
      icon={<Icon alt="Warning" id="Warning" />}
      message={confirmMessage}
      onConfirm={onConfirm}
      okText="Yes"
      cancelText="No"
      getPopupContainer={getPopupContainer}
    >
      <span onClick={(e) => e.stopPropagation()}>{label}</span>
    </ConfirmationPopup>
  ),
});

const onlyWithSortOrder = (sorter: SorterResult<TTEUser>) =>
  Boolean(sorter.order);

const createSortString = (sorter: SorterResult<TTEUser>) =>
  `${sorter.field}:${sorter.order === 'ascend' ? 'asc' : 'desc'}`;

const combineSorters = (sorters: SorterResult<TTEUser>[]) =>
  sorters.filter(onlyWithSortOrder).map(createSortString).join('&');
